'use client';

import { useListItem, useMergeRefs } from '@floating-ui/react';
import { type MouseEventHandler, forwardRef, useCallback, useContext, useId, useMemo } from 'react';

import { css, cx } from '@/styled-system/css';

import { ComboboxOptionContext } from './context/combobox-option.context';
import { ComboboxRootContext } from './context/combobox-root.context';
import { comboboxOptionRecipe } from './styles/combobox-option.styles';
import type { ComboboxOptionPropsInterface } from './types/combobox-option.types';

/**
 * @since 10.0.0
 */
export const ComboboxOption = forwardRef<HTMLDivElement, ComboboxOptionPropsInterface>(
  (props: ComboboxOptionPropsInterface, forwardedRef) => {
    const { className, css: cssProp, option, onClick, ...rest } = props;
    const { combobox } = useContext(ComboboxRootContext);

    const id = useId();

    const { ref: itemRef, index } = useListItem({
      label: typeof option === 'string' ? option : option?.label,
    });
    const mergedRefs = useMergeRefs([itemRef, forwardedRef]);

    const {
      value = option as string,
      disabled = false,
      ['aria-label']: ariaLabel,
    } = typeof option === 'object' ? option : { value: option };

    const isActive = useMemo(() => combobox.activeIndex === index, [combobox.activeIndex, index]);
    const isSelected = useMemo(
      () => combobox.selectedValues.some((selectedValue) => selectedValue === value),
      [combobox.selectedValues, value],
    );

    const styles = css(
      comboboxOptionRecipe.raw({ active: isActive, disabled, selected: !!isSelected }),
      cssProp,
    );

    const onClickCallback = useCallback<MouseEventHandler<HTMLDivElement>>(
      (event) => {
        combobox.onClickOption(value, event);
        onClick?.(event);
      },
      [value, combobox, onClick],
    );

    const contextValue = useMemo(
      () => ({ disabled, selected: isSelected }),
      [disabled, isSelected],
    );

    return (
      <ComboboxOptionContext.Provider value={contextValue}>
        <div
          data-c11n-component="Combobox.Option"
          data-c11n-version={__C11N_VERSION__}
          ref={mergedRefs}
          id={id}
          className={cx('c11n', 'c11n_combobox__option', 'group', styles, className)}
          role="option"
          aria-label={ariaLabel}
          aria-disabled={disabled === true ? disabled : undefined}
          aria-selected={isSelected}
          tabIndex={isActive && !disabled ? 0 : -1}
          {...combobox.getItemProps({
            onClick: disabled ? undefined : onClickCallback,
            onMouseDown: (event) => event.preventDefault(),
            ...rest,
          })}
        />
      </ComboboxOptionContext.Provider>
    );
  },
);

ComboboxOption.displayName = 'Combobox.Option';
